/**
 * O contrato com a API, em Zod.
 *
 * Validar na borda existe por um motivo concreto neste projeto: o painel monta
 * mapa e alerta a partir destes campos, e um `null` inesperado em `lat` vira
 * marcador em (0, 0) — no golfo da Guiné, com cara de veículo real. Falhar alto
 * na entrada é melhor que desenhar errado.
 *
 * Os nomes seguem a API, que é em inglês (CLAUDE.md). O que muda de idioma é o
 * texto para o usuário, nunca o identificador.
 */
import { z } from "zod";

/** Um veículo no snapshot da frota — o que o `WS /stream` e `/fleet/positions` mandam. */
export const vehicleSchema = z.object({
  imei: z.string(),
  vehicle: z.string().nullable(),
  /**
   * Endereço da posição atual, resolvido pelo processor via Nominatim. `null`
   * quando ainda não foi resolvido, o servidor está fora, ou o recurso está
   * desligado — os três casos aparecem igual para quem olha, e nenhum é erro.
   *
   * `.optional()` porque uma API anterior à migration 0011 não manda o campo, e
   * o painel não pode quebrar por causa disso.
   */
  address: z.string().nullish(),
  plate: z.string().nullable(),
  /**
   * Nome da base do veículo — o que o mapa e a lista mostram no lugar da placa
   * e do IMEI. `null` nos dois casos que viram "Sem base" na tela: equipamento
   * sem veículo vinculado, e veículo sem base atribuída.
   *
   * `.nullish()`, como o `address`: uma API anterior a esta mudança não manda o
   * campo, e o painel não pode se recusar a desenhar a frota por causa disso.
   */
  base: z.string().nullish(),
  /**
   * Litros por unidade do `can32bitR7` neste veículo (migration `0027`).
   *
   * Ausente vale o mesmo que `null`: NÃO AFERIDO. Nos dois casos a tela não
   * converte o combustível consumido, em vez de usar a escala do documento —
   * ela está certa em metade do parque medido e 500× errada na outra metade.
   */
  canFuelScaleL: z.number().nullish(),
  lat: z.number().nullable(),
  lon: z.number().nullable(),
  speed: z.number().nullable(),
  heading: z.number().nullable(),
  ignition: z.boolean().nullable(),
  supplyMv: z.number().nullable(),
  /** Bateria interna do rastreador. `nullish`: API anterior à 0025 não manda. */
  batteryMv: z.number().nullish(),
  /** Satélites do ÚLTIMO FIXO, não do último pacote (migration 0025). */
  satellites: z.number().nullish(),
  online: z.boolean().nullable(),
  deviceClock: z.string().nullable(),
  lastPacketAt: z.string().nullable(),
  /**
   * Distância entre a hora do DADO e a hora em que o equipamento falou conosco.
   * Grande = ele está despejando histórico, e a posição NÃO é onde ele está
   * agora. Visto em campo com 22 h de atraso.
   */
  delaySeconds: z.number().nullable(),
});

/**
 * Indicadores que mudam devagar: alerta na fila e motorista do momento.
 *
 * Vêm de `/fleet/indicators`, não do snapshot: aquele é a string já serializada
 * do Redis, pedida a cada poucos segundos, e alerta e motorista não mudam
 * nesse ritmo.
 */
export const fleetIndicatorSchema = z.object({
  imei: z.string(),
  /** Alertas que EXIGEM AÇÃO e seguem na fila, no dia operacional corrente. */
  openAlerts: z.number(),
  lastAlertAt: z.string().nullable(),
  driverId: z.string().nullable(),
  driver: z.string().nullable(),
  /**
   * De onde veio o motorista (RN-05). O banco recusa `driver_id` sem isto, e a
   * tela mostra junto: nome sem origem esconde que alguém digitou.
   */
  driverSource: z.enum(["manual", "rfid", "ticketing", "inferred"]).nullable(),
});

export const fleetIndicatorsSchema = z.object({
  generatedAt: z.string(),
  operationalDay: z.string(),
  vehicles: z.array(fleetIndicatorSchema),
});

export const fleetSnapshotSchema = z.object({
  generatedAt: z.string(),
  vehicles: z.array(vehicleSchema),
});

/** Mensagens do `WS /stream`. O snapshot vem primeiro; depois só quem mudou. */
export const streamMessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("snapshot"),
    data: fleetSnapshotSchema.nullable(),
    warning: z.string().optional(),
  }),
  z.object({
    type: z.literal("positions"),
    at: z.string(),
    vehicles: z.array(vehicleSchema),
  }),
]);

export const loginResponseSchema = z.object({
  token: z.string(),
  role: z.string(),
  expiresInSeconds: z.number(),
});

export const refreshResponseSchema = z.object({
  token: z.string(),
  expiresInSeconds: z.number(),
});

/** Uma linha de `telemetry`. `extras` só vem com `includeExtras=true`. */
export const telemetryRowSchema = z.looseObject({
  time: z.string(),
  record_number: z.number(),
  lat: z.number().nullable(),
  lon: z.number().nullable(),
  speed_kmh: z.number().nullable(),
  satellites: z.number().nullable(),
  // `odometer_m` e `can_odometer_m` são bigint no banco: o driver pode entregar
  // string para não perder precisão em número acima de 2^53.
  odometer_m: z.union([z.number(), z.string()]).nullable(),
  can_odometer_m: z.union([z.number(), z.string()]).nullish(),
  can_speed_kmh: z.number().nullish(),
  supply_mv: z.number().nullable(),
  battery_mv: z.number().nullish(),
  temperature_c: z.number().nullish(),
  altitude_m: z.number().nullish(),
  received_at: z.string().nullish(),
  /** Temperatura do óleo em décimos de °C (0015). Era `axle1_load_kg`, leitura errada do mesmo registrador. */
  oil_temp_dc: z.number().nullish(),
  /**
   * Condução e via (migration 0012), em centésimos de g. `nullish` porque
   * registro anterior à migration não tem — e o painel não pode quebrar por
   * causa de dado histórico.
   */
  eco_accel_cg: z.number().nullish(),
  eco_brake_cg: z.number().nullish(),
  eco_corner_cg: z.number().nullish(),
  eco_bump_cg: z.number().nullish(),
  /** CAN semântico (0013) — NULL enquanto a F8.1 não for executada no parque. */
  fuel_level_dpct: z.number().nullish(),
  coolant_temp_c: z.number().nullish(),
  engine_rpm: z.number().nullish(),
  can_fuel_used_l: z.number().nullish(),
  /** Pedal do acelerador em décimos de porcento (F8.3). 1000 = 100%. */
  accelerator_dpct: z.number().nullish(),
  extras: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const historyResponseSchema = z.object({
  imei: z.string(),
  rows: z.array(telemetryRowSchema),
  nextCursor: z.string().nullable(),
});

/** Uma fatia do período — dia ou hora, conforme a granularidade pedida. */
export const summaryBucketSchema = z.object({
  start: z.string(),
  records: z.number(),
  distanceM: z.number().nullable(),
  maxSpeed: z.number().nullable(),
  // Só a granularidade horária traz estes: a média de médias diárias não é a
  // média do dia, e a API se recusa a fingir que é.
  avgMovingSpeed: z.number().nullish(),
  avgSupplyMv: z.number().nullish(),
  minBatteryMv: z.number().nullish(),
});

/** `/devices/:imei/summary` — o horímetro sai daqui, dos ciclos de ignição. */
export const summaryResponseSchema = z.object({
  imei: z.string(),
  from: z.string(),
  to: z.string(),
  granularity: z.enum(["day", "hour"]),
  total: z.object({
    records: z.number(),
    distanceM: z.number().nullable(),
    distanceKm: z.number().nullable(),
    maxSpeed: z.number().nullable(),
    /**
     * **Objeto, não número.** A API devolve `{hours, cycles, note}` — eu havia
     * declarado `number` aqui por suposição, o Zod recusava a resposta inteira e
     * o relatório mostrava "não foi possível carregar" com a API respondendo 200.
     *
     * O `note` explica que um evento de ignição perdido desloca o total; ele vai
     * para a tela, porque é ressalva sobre o número e não sobre o código.
     */
    engineHours: z.object({
      hours: z.number().nullable(),
      cycles: z.number(),
      note: z.string().optional(),
    }),
  }),
  buckets: z.array(summaryBucketSchema),
});

/** Um alerta, como `/alerts` devolve. */
export const alertSchema = z.object({
  id: z.string(),
  kind: z.string(),
  time: z.string(),
  imei: z.string(),
  lat: z.number().nullable(),
  lon: z.number().nullable(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  geofence_id: z.string().nullable(),
  geofence: z.string().nullable(),
  vehicle: z.string().nullable(),
  plate: z.string().nullable(),
});

export const alertsResponseSchema = z.object({
  // `alerts`, não `rows`. O `/devices/:imei/telemetry` usa `rows` e eu supus
  // que este fosse igual — a API é que manda, e ela diz `alerts`.
  alerts: z.array(alertSchema),
  nextCursor: z.string().nullable(),
});

/** Uma cerca. A `box` é a caixa envolvente que a API calcula. */
export const geofenceSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(["circle", "polygon", "line"]),
  /** O que a cerca é (migration 0026). `nullish` porque API antiga não manda. */
  purpose: z.enum(["point", "terminal", "garage", "route", "other"]).nullish(),
  /** Cor no mapa, `#RRGGBB`. `null` = a cor padrão do painel. */
  color: z.string().nullish(),
  /**
   * Cor do NOME da cerca no mapa, `#RRGGBB` (migration 0029).
   *
   * Separada de `color`, que pinta traço e preenchimento: uma cerca amarela
   * sobre satélite precisa de traço amarelo para se distinguir das vizinhas E
   * de texto escuro para ser legível. `null` usa a cor de texto do tema.
   */
  label_color: z.string().nullish(),
  center_lat: z.number().nullable(),
  center_lon: z.number().nullable(),
  radius_m: z.number().nullable(),
  /**
   * Os vértices, como a API grava e devolve: array de `[longitude, latitude]`.
   *
   * Era `z.unknown()`, e foi por isso que o painel passou meses desenhando
   * polígono nenhum em silêncio: sem a borda declarar a forma, cada leitor
   * adivinhava — e os dois adivinhavam `{ coordinates: [anel] }`, que a API
   * nunca mandou. Ver `lib/geofence.ts`.
   *
   * O par é de DOIS números porque é o que a API valida ao receber
   * (`geofences.controller.ts`, "vértice N inválido: esperado [longitude,
   * latitude]"). Declarar igual aqui faz os dois lados falharem pelo mesmo
   * motivo, em vez de um aceitar e o outro desenhar errado.
   */
  polygon: z.array(z.tuple([z.number(), z.number()])).nullable(),
  /**
   * Largura TOTAL da faixa, em metros, só em `kind: "line"` (migration 0028).
   *
   * `nullish` porque API anterior à 0028 não manda o campo, e porque círculo e
   * polígono não têm largura nenhuma.
   */
  width_m: z.number().nullish(),
  /**
   * Os três controles da cerca (migration 0031).
   *
   * Nos dois primeiros, `null` quer dizer **não controlar** — e é por isso que
   * eles são anuláveis em vez de ter padrão: um padrão aqui faria o painel
   * mostrar um limite que o servidor não aplica. `nullish` porque API anterior
   * à 0031 não manda os campos.
   */
  speed_limit_kmh: z.number().nullish(),
  max_dwell_minutes: z.number().nullish(),
  /** Gera entrada e saída. A permanência e a velocidade não dependem disto. */
  track_entry_exit: z.boolean().nullish(),
  active: z.boolean(),
  created_at: z.string().nullish(),
  box: z.unknown().nullish(),
});

/**
 * Busca de endereço. `enabled: false` quando o servidor não tem Nominatim
 * configurado — e aí a tela avisa, em vez de o cadastro parecer quebrado.
 */
export const geocodeSchema = z.object({
  enabled: z.boolean(),
  error: z.string().nullish(),
  places: z.array(
    z.object({
      label: z.string(),
      lat: z.number(),
      lon: z.number(),
      box: z
        .object({ minLat: z.number(), maxLat: z.number(), minLon: z.number(), maxLon: z.number() })
        .nullable(),
    }),
  ),
});

export const geofenceListSchema = z.array(geofenceSchema);

/** `/vehicles` — o seletor do relatório. */
export const vehicleListSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string().nullable(),
    plate: z.string().nullable(),
    imei: z.string().nullable(),
    system_voltage: z.number().nullish(),
    speed_limit_kmh: z.number().nullish(),
  }),
);

export type Vehicle = z.infer<typeof vehicleSchema>;
export type FleetSnapshot = z.infer<typeof fleetSnapshotSchema>;
export type StreamMessage = z.infer<typeof streamMessageSchema>;
export type TelemetryRow = z.infer<typeof telemetryRowSchema>;
export type SummaryResponse = z.infer<typeof summaryResponseSchema>;
export type SummaryBucket = z.infer<typeof summaryBucketSchema>;
export type FleetVehicle = z.infer<typeof vehicleListSchema>[number];
export type Alert = z.infer<typeof alertSchema>;
export type Geofence = z.infer<typeof geofenceSchema>;


/**
 * Situação dos vínculos (`GET /device-links`, só `admin`).
 *
 * `pending` é equipamento que está comunicando e **não pertence a ninguém** —
 * ele não aparece no mapa, na lista nem gera alerta, porque tudo isso filtra por
 * cliente. É a lista que explica "tenho 10 rastreadores no ar e vejo 4".
 */
export const deviceLinkSchema = z.object({
  imei: z.string(),
  status: z.string(),
  first_seen_at: z.string().nullish(),
  last_seen_at: z.string().nullish(),
  online: z.boolean().nullish(),
  last_packet_at: z.string().nullish(),
  /** Só em `linked`: pendente não tem veículo porque não tem dono. */
  vehicle: z.string().nullish(),
  plate: z.string().nullish(),
});

export const deviceLinksResponseSchema = z.object({
  linked: z.array(deviceLinkSchema),
  pending: z.array(deviceLinkSchema),
});

export type DeviceLink = z.infer<typeof deviceLinkSchema>;
export type DeviceLinksResponse = z.infer<typeof deviceLinksResponseSchema>;

/** Resposta sem corpo útil — o vínculo é confirmado pelo 2xx, não pelo JSON. */
export const okSchema = z.unknown();

// ===================================================== cadastro (F9)

/**
 * As listagens do cadastro.
 *
 * `nullish()` em quase tudo não é desleixo: base, garagem e modelo são nulos em
 * toda a frota de hoje, e vão continuar nulos até alguém preencher. Schema que
 * exigisse esses campos faria a tela de cadastro quebrar exatamente no estado em
 * que ela é mais necessária — o primeiro dia.
 */
export const baseSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  active: z.boolean(),
  created_at: z.string().nullish(),
  vehicles: z.number(),
  drivers: z.number(),
  garages: z.number(),
});

export const garageSchema = z.object({
  id: z.string(),
  name: z.string(),
  active: z.boolean(),
  baseId: z.string(),
  baseCode: z.string().nullish(),
  baseName: z.string().nullish(),
  geofenceId: z.string().nullish(),
  geofenceName: z.string().nullish(),
});

export const vehicleModelSchema = z.object({
  id: z.string(),
  name: z.string(),
  manufacturer: z.string().nullish(),
  active: z.boolean(),
  vehicles: z.number(),
  /** Perfil de condução ecológica que todo veículo deste modelo herda. */
  eco_profile_id: z.string().nullish(),
});

/**
 * Perfil de condução ecológica.
 *
 * O mapa que diz qual código, em qual slot de `userData`, é qual evento. Existe
 * porque o slot não significa a mesma coisa em dois ônibus — ver
 * `docs/CONDUCAO-ECOLOGICA.md`.
 */
export const ecoProfileSchema = z.object({
  id: z.string(),
  name: z.string(),
  notes: z.string().nullish(),
  active: z.boolean(),
  /** Quantas regras tem. Zero é perfil cadastrado que não gera evento nenhum. */
  rules: z.number(),
  models: z.number(),
  vehicles: z.number(),
});

export const ecoRuleSchema = z.object({
  slot: z.number(),
  code: z.number(),
  event_key: z.string(),
  label: z.string(),
  category: z.enum(["eco", "safety"]),
  score: z.number(),
  min_duration_s: z.number().nullish(),
  max_duration_s: z.number().nullish(),
  min_speed_kmh: z.number().nullish(),
  max_speed_kmh: z.number().nullish(),
});

/**
 * `identification` diz de onde vem a identificação deste motorista.
 *
 * `inferred` é o valor de 100% da frota hoje — nenhum equipamento tem chave
 * iButton cadastrada e a tag não vem no pacote. A tela mostra isso em vez de
 * deixar o campo vazio parecer "ok".
 */
export const LICENSE_CATEGORIES = ["ACC", "A", "B", "AB", "C", "AC", "D", "AD", "E", "AE"] as const;
export const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"] as const;

export const driverSchema = z.object({
  id: z.string(),
  registration: z.string(),
  name: z.string(),
  base_id: z.string().nullish(),
  baseCode: z.string().nullish(),
  baseName: z.string().nullish(),
  ibutton_key: z.string().nullish(),
  badge_id: z.string().nullish(),
  active: z.boolean(),
  identification: z.enum(["ibutton", "badge", "inferred"]),
  // Ficha (0021).
  phone: z.string().nullish(),
  email: z.string().nullish(),
  /** Número da CNH. */
  document: z.string().nullish(),
  license_category: z.enum(LICENSE_CATEGORIES).nullish(),
  /** `AAAA-MM-DD`, sem hora: documento é dia do calendário, não instante. */
  license_valid_until: z.string().nullish(),
  /**
   * Dias que faltam para a CNH vencer, pelo relógio do BANCO. Negativo = venceu.
   *
   * Vem pronto para a tela não comparar data com o relógio do navegador — um
   * computador com a data errada diria que está tudo em dia.
   */
  licenseDaysLeft: z.number().nullish(),
  blood_type: z.enum(BLOOD_TYPES).nullish(),
  emergency_contact: z.string().nullish(),
  notes: z.string().nullish(),
});

export const registryVehicleSchema = z.object({
  id: z.string(),
  name: z.string(),
  plate: z.string().nullish(),
  imei: z.string().nullish(),
  system_voltage: z.number().nullish(),
  speed_limit_kmh: z.number().nullish(),
  base_id: z.string().nullish(),
  garage_id: z.string().nullish(),
  model_id: z.string().nullish(),
  /** Perfil de condução próprio. `null` = herda o do modelo. */
  eco_profile_id: z.string().nullish(),
  baseCode: z.string().nullish(),
  baseName: z.string().nullish(),
  garageName: z.string().nullish(),
  modelName: z.string().nullish(),
});

export const userSchema = z.object({
  id: z.string(),
  email: z.string().nullish(),
  name: z.string().nullish(),
  role: z.enum(["admin", "operator", "viewer", "instructor", "supervisor"]),
  base_id: z.string().nullish(),
  baseCode: z.string().nullish(),
  baseName: z.string().nullish(),
  active: z.boolean(),
  created_at: z.string().nullish(),
  unit: z.string().nullish(),
  department: z.string().nullish(),
  phone: z.string().nullish(),
  /** Usuário temporário. `null` = não expira. */
  expires_at: z.string().nullish(),
  /**
   * Veio do relógio do BANCO, não do navegador.
   *
   * Comparar `expires_at` com `Date.now()` aqui faria um computador com a hora
   * errada mostrar acesso que não existe mais — ou esconder um que ainda vale.
   */
  expired: z.boolean().nullish(),
  profile_id: z.string().nullish(),
  profileName: z.string().nullish(),
});

/** Perfil de acesso. `system` é o que o banco não deixa apagar. */
export const profileSchema = z.object({
  id: z.string(),
  name: z.string(),
  system: z.boolean(),
});
export const profileListSchema = z.array(profileSchema);

/**
 * `GET /auth/me` — quem a credencial atual representa.
 *
 * O painel usa isto para RECUPERAR o papel depois de um F5: o token sobrevive
 * no `sessionStorage`, o papel não vivia em lugar nenhum, e sem ele as abas de
 * administrador sumiam da tela com a sessão ainda válida.
 */
export const meSchema = z.object({
  clientId: z.string(),
  role: z.enum(["admin", "operator", "viewer", "instructor", "supervisor"]),
  via: z.string(),
  userId: z.string().nullish(),
  userEmail: z.string().nullish(),
  baseId: z.string().nullish(),
});

export const clientSchema = z.object({
  id: z.string(),
  name: z.string(),
  document: z.string().nullish(),
  active: z.boolean(),
});

/**
 * `source` distingue "o cliente escolheu 03h" de "ninguém escolheu e o padrão é
 * 03h". Parecem a mesma coisa na tela e não são: a segunda é um parâmetro que
 * ninguém conferiu.
 */
export const clientSettingsSchema = z.object({
  operationalDayStart: z.string(),
  timezone: z.string(),
  /** Regra D-3 (RF-OP-03): horas sem transmitir que contam como sem comunicação. */
  noCommHours: z.number(),
  /** Pacote de dados mensal de cada equipamento, em MB. `null` = não informado. */
  dataPlanMb: z.number().nullish(),
  /** Acréscimo % sobre o byte medido, para aproximar o cobrado pela operadora. */
  trafficOverheadPct: z.number().nullish(),
  /**
   * Os limiares de alerta (migrations 0019, 0032 e 0033).
   *
   * Todos obrigatórios, ao contrário do plano de dados: **não existe "não
   * informado"** num limiar. Limiar nulo seria um alerta ligado que nunca
   * dispara — o que desliga um alerta é o interruptor em Tipos de alerta.
   *
   * As unidades são as das colunas de telemetria: centésimos de g, décimos de
   * grau, décimos de por cento. A tela converte para exibir; o contrato não.
   */
  idleMinSeconds: z.number(),
  idleSpeedKmh: z.number(),
  harshAccelCg: z.number(),
  harshBrakeCg: z.number(),
  overRevRpm: z.number(),
  oilTempHighDc: z.number(),
  accelPedalHighDpct: z.number(),
  accelPedalHighSeconds: z.number(),
  /**
   * Os quatro parâmetros do motor de regras (migration 0034).
   *
   * **Anuláveis, e aqui `null` tem significado**: é "use o padrão em vigor",
   * que mora no processor e pode vir de variável de ambiente. A API devolve
   * `null` em vez de repetir o número justamente para não afirmar um padrão que
   * o serviço talvez tenha trocado — a tela mostra o número como *placeholder*,
   * de `ENGINE_DEFAULTS`.
   */
  alertHysteresisSeconds: z.number().nullish(),
  overspeedClearSeconds: z.number().nullish(),
  alertRepeatSeconds: z.number().nullish(),
  movingSpeedKmh: z.number().nullish(),
  updatedAt: z.string().nullish(),
  source: z.enum(["client", "default"]),
});

// ------------------------------------------------ consumo de dados do chip

export const TRAFFIC_STATUS = ["ok", "atencao", "estouro", "sem_plano"] as const;

export const trafficDeviceSchema = z.object({
  imei: z.string(),
  vehicle: z.string().nullish(),
  plate: z.string().nullish(),
  bytesIn: z.number(),
  bytesOut: z.number(),
  bytes: z.number(),
  packets: z.number(),
  /** O medido mais o acréscimo de rede. Igual a `bytes` quando não há acréscimo. */
  billableBytes: z.number(),
  /** Consumido ÷ dias decorridos × dias do mês. */
  projectedBytes: z.number(),
  usedPct: z.number().nullable(),
  projectedPct: z.number().nullable(),
  status: z.enum(TRAFFIC_STATUS),
});

export const trafficMonthlySchema = z.object({
  month: z.string(),
  daysInMonth: z.number(),
  daysElapsed: z.number(),
  planMb: z.number().nullable(),
  planBytes: z.number().nullable(),
  overheadPct: z.number().nullable(),
  bytesPerMb: z.number(),
  attentionPct: z.number(),
  devices: z.array(trafficDeviceSchema),
  totals: z.object({
    devices: z.number(),
    bytes: z.number(),
    billableBytes: z.number(),
    projectedBytes: z.number(),
    overPlan: z.number(),
    attention: z.number(),
    silent: z.number(),
  }),
});

export const trafficDailySchema = z.object({
  from: z.string(),
  to: z.string(),
  imei: z.string().nullable(),
  days: z.array(
    z.object({ day: z.string(), bytesIn: z.number(), bytesOut: z.number(), bytes: z.number(), packets: z.number() }),
  ),
});

/** Resposta do envio por e-mail: quantos endereços e qual arquivo foi. */
export const auditEmailSchema = z.object({
  sent: z.number(),
  rows: z.number(),
  file: z.string(),
});

export const auditEntrySchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  at: z.string(),
  actor: z.string(),
  user_id: z.string().nullish(),
  action: z.enum(["create", "update", "delete"]),
  entity: z.string(),
  entity_id: z.string(),
  before: z.unknown().nullish(),
  after: z.unknown().nullish(),
});

export const auditResponseSchema = z.object({
  rows: z.array(auditEntrySchema),
  /** A janela que o SERVIDOR resolveu, no fuso do cliente. Nula = sem filtro de data. */
  window: z.object({ from: z.string(), to: z.string(), timezone: z.string() }).nullish(),
  nextCursor: z.string().nullable(),
});

export const baseListSchema = z.array(baseSchema);
export const garageListSchema = z.array(garageSchema);
export const vehicleModelListSchema = z.array(vehicleModelSchema);
export const ecoProfileListSchema = z.array(ecoProfileSchema);
export const ecoRuleListSchema = z.array(ecoRuleSchema);
export const driverListSchema = z.array(driverSchema);
export const registryVehicleListSchema = z.array(registryVehicleSchema);
export const userListSchema = z.array(userSchema);

export type Profile = z.infer<typeof profileSchema>;
export type Base = z.infer<typeof baseSchema>;
export type Garage = z.infer<typeof garageSchema>;
export type VehicleModel = z.infer<typeof vehicleModelSchema>;
export type EcoProfile = z.infer<typeof ecoProfileSchema>;
export type EcoRule = z.infer<typeof ecoRuleSchema>;
export type Driver = z.infer<typeof driverSchema>;
export type RegistryVehicle = z.infer<typeof registryVehicleSchema>;
export type User = z.infer<typeof userSchema>;
export type Client = z.infer<typeof clientSchema>;
export type ClientSettings = z.infer<typeof clientSettingsSchema>;
export type FleetIndicator = z.infer<typeof fleetIndicatorSchema>;
export type GeocodeResult = z.infer<typeof geocodeSchema>;
export type Place = GeocodeResult["places"][number];
export type TrafficMonthly = z.infer<typeof trafficMonthlySchema>;
export type TrafficDevice = z.infer<typeof trafficDeviceSchema>;
export type TrafficDaily = z.infer<typeof trafficDailySchema>;
export type TrafficStatus = (typeof TRAFFIC_STATUS)[number];
export type AuditEntry = z.infer<typeof auditEntrySchema>;

// ------------------------------------------------------ F10: fila e comunicação

export const TREATMENT_STATUS = ["open", "in_progress", "done", "discarded"] as const;
export const treatmentStatusSchema = z.enum(TREATMENT_STATUS);

/**
 * Uma linha da fila de tratativas.
 *
 * Quase tudo é `nullish` porque quase tudo pode faltar de verdade: o alerta
 * chega por IMEI, e um IMEI sem veículo cadastrado — ou um veículo sem base, sem
 * garagem e sem modelo — é o estado normal de um parque que está sendo
 * cadastrado agora. A tela mostra "— sem dado" e segue; esconder a linha
 * esconderia justamente o alerta do equipamento que ninguém cadastrou.
 */
export const queueAlertSchema = z.object({
  id: z.string(),
  kind: z.string(),
  time: z.string(),
  imei: z.string(),
  lat: z.number().nullish(),
  lon: z.number().nullish(),
  payload: z.unknown().nullish(),
  geofence: z.string().nullish(),
  vehicleId: z.string().nullish(),
  vehicle: z.string().nullish(),
  plate: z.string().nullish(),
  base: z.string().nullish(),
  baseId: z.string().nullish(),
  garage: z.string().nullish(),
  model: z.string().nullish(),
  status: treatmentStatusSchema,
  assigneeId: z.string().nullish(),
  assignee: z.string().nullish(),
  assigneeEmail: z.string().nullish(),
  note: z.string().nullish(),
  actor: z.string().nullish(),
  treatedAt: z.string().nullish(),
});

export const queueResponseSchema = z.object({
  day: z.string(),
  from: z.string(),
  to: z.string(),
  operationalDayStart: z.string(),
  timezone: z.string(),
  counts: z.object({
    open: z.number(),
    in_progress: z.number(),
    done: z.number(),
    discarded: z.number(),
  }),
  alerts: z.array(queueAlertSchema),
  nextCursor: z.string().nullable(),
});

export const treatmentSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  status: treatmentStatusSchema,
  note: z.string().nullish(),
  actor: z.string(),
  at: z.string(),
  assigneeId: z.string().nullish(),
  assignee: z.string().nullish(),
});

export const treatmentHistorySchema = z.object({
  alertId: z.string(),
  treatments: z.array(treatmentSchema),
});

/**
 * Tipos de alerta, com as DUAS perguntas (migration 0030).
 *
 * `enabled` decide se o alerta é gerado; `actionable`, se o alerta gerado entra
 * na fila. `required` é o tipo que não pode ser desligado — hoje só as
 * transições de ignição, porque a viagem é construída a partir delas.
 */
export const alertKindSchema = z.object({
  kind: z.string(),
  category: z.enum(["tracking", "maintenance", "eco_driving"]),
  enabled: z.boolean(),
  actionable: z.boolean(),
  required: z.boolean(),
  source: z.enum(["client", "default"]),
});

export const alertKindsSchema = z.object({ kinds: z.array(alertKindSchema) });

export const communicationStatusSchema = z.enum(["ok", "no_comm", "never", "unlinked"]);

export const communicationSchema = z.object({
  noCommHours: z.number(),
  transmittingPercent: z.number().nullable(),
  totals: z.object({
    ok: z.number(),
    no_comm: z.number(),
    never: z.number(),
    unlinked: z.number(),
    total: z.number(),
  }),
  byBase: z.array(
    z.object({
      baseId: z.string().nullish(),
      base: z.string().nullish(),
      ok: z.number(),
      no_comm: z.number(),
      never: z.number(),
      unlinked: z.number(),
      total: z.number(),
    }),
  ),
  vehicles: z.array(
    z.object({
      vehicleId: z.string(),
      vehicle: z.string(),
      plate: z.string().nullish(),
      imei: z.string().nullish(),
      base: z.string().nullish(),
      garage: z.string().nullish(),
      lastPacketAt: z.string().nullish(),
      online: z.boolean().nullish(),
      status: communicationStatusSchema,
      silentHours: z.number().nullish(),
    }),
  ),
});

export type TreatmentStatus = z.infer<typeof treatmentStatusSchema>;
export type QueueAlert = z.infer<typeof queueAlertSchema>;
export type QueueResponse = z.infer<typeof queueResponseSchema>;
export type Treatment = z.infer<typeof treatmentSchema>;
/**
 * Canal de entrega (RF-OP-08).
 *
 * `secret` só vem na criação e na rotação — a listagem manda `hasSecret`, que é
 * "existe um segredo" e não o segredo. Por isso ele é opcional aqui: um schema
 * que o exigisse quebraria a listagem, e um que o devolvesse sempre estaria
 * mentindo sobre o que a API faz.
 */
export const channelSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(["email", "webhook"]),
  target: z.string(),
  kinds: z.array(z.string()),
  baseId: z.string().nullish(),
  base: z.string().nullish(),
  active: z.boolean(),
  hasSecret: z.boolean(),
  secret: z.string().optional(),
  lastStatus: z.enum(["pending", "sent", "failed"]).nullish(),
  lastError: z.string().nullish(),
  lastAt: z.string().nullish(),
  failed: z.coerce.number().nullish(),
});

export const channelsSchema = z.object({ channels: z.array(channelSchema) });

export const deliverySchema = z.object({
  id: z.coerce.string(),
  kind: z.string(),
  status: z.enum(["pending", "sent", "failed"]),
  attempts: z.number(),
  lastError: z.string().nullish(),
  alertId: z.string(),
  alertTime: z.string(),
  sentAt: z.string().nullish(),
  nextAttemptAt: z.string(),
  createdAt: z.string(),
});

export const deliveriesSchema = z.object({ deliveries: z.array(deliverySchema) });

export const channelTestSchema = z.object({
  ok: z.boolean(),
  deliveryId: z.coerce.string().nullish(),
  channel: z.string(),
  note: z.string(),
});

export const retrySchema = z.object({ ok: z.boolean(), requeued: z.number() });

/**
 * Viagem (RF-OP-06).
 *
 * `distanceM` chega como string: é `bigint` no Postgres, e o driver não
 * arredonda bigint para `number` por conta própria — nem deveria. `coerce`
 * resolve na borda, uma vez, em vez de em cada soma na tela.
 */
export const tripSchema = z.object({
  id: z.string(),
  imei: z.string(),
  startedAt: z.string(),
  endedAt: z.string().nullish(),
  closedBy: z.enum(["ignition", "silence"]).nullish(),
  distanceM: z.coerce.number().nullish(),
  fuelL: z.number().nullish(),
  maxSpeedKmh: z.number().nullish(),
  idleSeconds: z.number().nullish(),
  computedAt: z.string().nullish(),
  startLat: z.number().nullish(),
  startLon: z.number().nullish(),
  endLat: z.number().nullish(),
  endLon: z.number().nullish(),
  driverId: z.string().nullish(),
  driver: z.string().nullish(),
  driverRegistration: z.string().nullish(),
  driverSource: z.enum(["manual", "rfid", "ticketing", "inferred"]).nullish(),
  driverActor: z.string().nullish(),
  driverSetAt: z.string().nullish(),
  vehicleId: z.string().nullish(),
  vehicle: z.string().nullish(),
  plate: z.string().nullish(),
  base: z.string().nullish(),
  baseId: z.string().nullish(),
  garage: z.string().nullish(),
  model: z.string().nullish(),
});

export const tripsResponseSchema = z.object({
  day: z.string(),
  from: z.string(),
  to: z.string(),
  operationalDayStart: z.string(),
  timezone: z.string(),
  totals: z.object({
    trips: z.number(),
    open: z.number(),
    distanceKm: z.number(),
    idleMinutes: z.number(),
    fuelL: z.number(),
    tripsWithFuel: z.number(),
    kmPerL: z.number().nullish(),
  }),
  trips: z.array(tripSchema),
});

export type AlertKinds = z.infer<typeof alertKindsSchema>;
export type AlertKindSetting = z.infer<typeof alertKindSchema>;
export type Communication = z.infer<typeof communicationSchema>;
export type Channel = z.infer<typeof channelSchema>;
export type Trip = z.infer<typeof tripSchema>;
export type TripsResponse = z.infer<typeof tripsResponseSchema>;
export type Delivery = z.infer<typeof deliverySchema>;
export type CommunicationStatus = z.infer<typeof communicationStatusSchema>;
